import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

/**
 * Lightweight service to share auth state without circular dependencies
 * This service has NO dependencies, so it's safe to inject in interceptors
 */
@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private readonly authInitializedSubject = new ReplaySubject<boolean>(1);
  public readonly authInitialized$ = this.authInitializedSubject.asObservable();
  
  /**
   * Track if auth was ever successfully completed in this session
   * This helps differentiate between:
   * - 401 from auth endpoints (auth failed) → redirect to login
   * - 401 from business APIs after auth succeeded → redirect to error page (not login)
   */
  private _authWasSuccessful = false;

  /**
   * Signal that auth initialization is complete
   * @param authenticated Whether the user is authenticated
   */
  setAuthInitialized(authenticated: boolean): void {
    if (authenticated) {
      this._authWasSuccessful = true;
    }
    this.authInitializedSubject.next(authenticated);
  }

  /**
   * Check if auth was ever successfully completed in this session
   * Used by interceptors to determine if 401 from business APIs
   * should redirect to error page instead of login
   */
  get authWasSuccessful(): boolean {
    return this._authWasSuccessful;
  }

  /**
   * Reset auth successful state (used during logout or forced re-auth)
   */
  resetAuthState(): void {
    this._authWasSuccessful = false;
  }
}
